// src/pages/Lavorazioni.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { downloadLavorazioniXlsx } from '../utils/exportLavorazioni';
import { Card } from '../components/ui';

const API_BASE = '/api';

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatUserLabel = (u) => {
  if (!u) return '';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  if (full) return full;
  return u.username || '';
};


const inDateRange = (valueIso, fromStr, toStr) => {
  if (!fromStr && !toStr) return true;
  if (!valueIso) return false;

  const v = new Date(valueIso);
  if (Number.isNaN(v.getTime())) return false;
  const valueTime = v.setHours(0, 0, 0, 0);

  if (fromStr) {
    const f = new Date(fromStr);
    if (Number.isNaN(f.getTime())) return false;
    if (valueTime < f.setHours(0, 0, 0, 0)) return false;
  }

  if (toStr) {
    const t = new Date(toStr);
    if (Number.isNaN(t.getTime())) return false;
    if (valueTime > t.setHours(23, 59, 59, 999)) return false;
  }

  return true;
};

// costruisce codice interno visibile con progressivo
const formatInternalCode = (order) => {
  if (!order) return '-';

  const base =
    order.internalCode ||
    order.codiceInterno ||
    (order.workOrder && order.workOrder.internalCode) ||
    '-';

  const iter =
    typeof order.iteration === 'number'
      ? order.iteration
      : typeof order.workOrder?.iteration === 'number'
      ? order.workOrder.iteration
      : 0;

  return iter > 0 ? `${base}-${iter}` : base;
};

// etichetta + classi colore per stato
const getStatusInfo = (order) => {
  const status = order.status || 'in_corso';
  const iter =
    typeof order.iteration === 'number' ? order.iteration : 0;

  if (status === 'chiusa') {
    return {
      label: 'Terminata',
      className: 'bg-gray-100 text-gray-700 border border-gray-300',
    };
  }

  if (status === 'sospesa') {
    return {
      label: 'Sospesa',
      className: 'bg-amber-100 text-amber-800 border border-amber-200',
    };
  }

  if (
    order.assignedTo?.nome === 'Da' &&
    order.assignedTo?.cognome === 'Assegnare'
  ) {
    return {
      label: 'Da assegnare',
      className: 'bg-orange-100 text-orange-800 border border-orange-200',
    };
  }

  if (status === 'in_corso') {
    if (iter > 0) {
      return {
        label: 'Riassegnata',
        className:
          'bg-purple-100 text-purple-800 border border-purple-200',
      };
    }
    return {
      label: 'Attiva',
      className:
        'bg-emerald-100 text-emerald-800 border border-emerald-200',
    };
  }

  return {
    label: status,
    className: 'bg-gray-100 text-gray-700 border border-gray-300',
  };
};

const Lavorazioni = () => {
  const navigate = useNavigate();

  // Proposte dalla casella assistenza: la card la vedono solo i ruoli che
  // possono revisionarle, col numero di quelle in attesa
  const canReviewGenerated = ['admin', 'segreteria', 'gestione'].includes(
    localStorage.getItem('role') || ''
  );
  const [generatedPending, setGeneratedPending] = useState(null);
  useEffect(() => {
    if (!canReviewGenerated) return;
    authFetch(`${API_BASE}/generated-work/summary`)
      .then((res) => (res && res.ok ? res.json() : null))
      .then((data) => {
        if (data) setGeneratedPending(data.daRevisionare ?? 0);
      })
      .catch(() => {});
  }, [canReviewGenerated]);

  const [orders, setOrders] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [errorTable, setErrorTable] = useState('');

const [filters, setFilters] = useState({
  q: '',                 // 🔥 barra unica
  tipo: 'tutti',         // tutti | intervento | manutenzione
  statoPreventivo: 'tutti', // tutti | Preventivo da fare | Preventivo fatto
  dataRicezioneDa: '',
  dataRicezioneA: '',
  dataEseguitaDa: '',
  dataEseguitaA: '',
});


  // carica tutte le lavorazioni (incluse chiuse)
  const loadOrders = async () => {
    setLoadingTable(true);
    setErrorTable('');
    try {
      const res = await authFetch(`${API_BASE}/work/orders`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel caricamento delle lavorazioni.'
        );
      }
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore loadOrders:', err);
      setErrorTable(err.message || 'Errore nella richiesta al server.');
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Applico filtri
  const filteredOrders = useMemo(() => {
    return (orders || []).filter((o) => {
      const codiceCompleto = formatInternalCode(o);

      // prendiamo SEMPRE l'ULTIMO evento:
      const eventsArray = Array.isArray(o.events) ? o.events : [];
      const lastEvent =
        eventsArray.length > 0
          ? eventsArray[eventsArray.length - 1]
          : null;

      const tecnicoName = lastEvent?.assignedTo
        ? formatUserLabel(lastEvent.assignedTo)
        : formatUserLabel(o.assignedTo);

      const dataIntervento =
        lastEvent?.date ||
        (Array.isArray(o.dates) && o.dates[0]) ||
        o.dateIntervento ||
        o.dataIntervento ||
        null;

      const dataRicezione =
        o.receivedAt || o.dateReceived || o.dataRicezione || null;

      const preventivoVal = o.preventivoStatus || o.statoPreventivo || '';

      const q = (filters.q || '').trim().toLowerCase();

      if (filters.tipo !== 'tutti' && (o.type || '') !== filters.tipo) return false;

      if (filters.statoPreventivo !== 'tutti') {
        if ((o.type || '') !== 'intervento') return false;
        if ((preventivoVal || '') !== filters.statoPreventivo) return false;
      }

      if (
        !inDateRange(dataRicezione, filters.dataRicezioneDa, filters.dataRicezioneA)
      ) return false;

      if (
        !inDateRange(dataIntervento, filters.dataEseguitaDa, filters.dataEseguitaA)
      ) return false;

      // 🔥 barra unica: match su campi principali
      if (q) {
        const hay = [
          codiceCompleto,
          o.externalCode,
          o.companyName || o.cliente,
          o.place || o.luogo,
          o.equipmentType || o.tipoApparecchiatura,
          o.equipmentMaker || o.produttore,
          o.equipmentModel || o.modello,
          o.equipmentSerial || o.seriale,
          o.equipmentInventory || o.inventario,
          o.equipmentPresidio || o.presidio,
          tecnicoName,
          preventivoVal,
          o.orderStatus,
          o.materialeArrivato,
          o.description,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
      
        if (!hay.includes(q)) return false;
      }

      return true;

    });
  }, [orders, filters]);

  // Stesse colonne, stesso ordine del foglio della segreteria: vedi
  // utils/exportLavorazioni.js
  const exportToExcel = () => {
    if (!filteredOrders.length) {
      alert('Nessun dato da esportare.');
      return;
    }
    downloadLavorazioniXlsx(filteredOrders, 'lavorazioni.xlsx');
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* Header */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
                Lavorazioni
              </h1>
              <p className="text-xs md:text-sm text-gray-500">
                Gestisci la creazione, la modifica e la consultazione delle
                lavorazioni assegnate a tecnici e responsabili.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="hidden md:inline-flex px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
            >
              ← Torna alla dashboard
            </button>
          </div>
        </Card>

        {/* Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          {/* Crea e assegna */}
          <Card className="shadow-md p-4 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Crea e assegna
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Crea una nuova lavorazione, definisci i dettagli e assegnala ad
                un tecnico o responsabile.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/lavorazioni/crea')}
              className="mt-auto px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition"
            >
              Crea e assegna
            </button>
          </Card>

          {/* Modifica lavorazioni */}
          <Card className="shadow-md p-4 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Modifica lavorazioni
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Gestisci le lavorazioni esistenti: modifica luogo, descrizione,
                date, assegnatario e consulta il calendario globale.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/lavorazioni/modifica')}
              className="mt-auto px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition"
            >
              Gestisci lavorazioni
            </button>
          </Card>

          {/* Lavorazioni assegnate */}
          <Card className="shadow-md p-4 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Lavorazioni assegnate
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Visualizza il calendario e tutti i dettagli delle lavorazioni
                assegnate al tuo account.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/lavorazioni/assegnate')}
              className="mt-auto px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition"
            >
              Vedi lavorazioni assegnate
            </button>
          </Card>

          {/* Lavorazioni in corso */}
          <Card className="shadow-md p-4 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Lavorazioni in corso
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Visualizza tutte le lavorazioni attualmente attive e non ancora
                completate.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/lavorazioni/in-corso')}
              className="mt-auto px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition"
            >
              Vedi lavorazioni in corso
            </button>
          </Card>
          {/* Lavorazioni aperte */}
          <Card className="shadow-md p-4 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Lavorazioni aperte
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Lavorazioni non ancora assegnate ad alcun tecnico.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/lavorazioni/aperte')}
              className="mt-auto px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition"
            >
              Vedi lavorazioni aperte
            </button>
          </Card>

          {/* Lavorazioni terminate */}
          <Card className="shadow-md p-4 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Lavorazioni terminate
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Consulta tutte le lavorazioni concluse, con dettagli e storico
                interventi.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/lavorazioni/terminate')}
              className="mt-auto px-4 py-2 rounded-lg bg-gray-700 text-white text-sm font-medium hover:bg-gray-800 transition"
            >
              Vedi lavorazioni terminate
            </button>
          </Card>

          {/* Lavorazioni generate dalle mail */}
          {canReviewGenerated && (
            <Card className="shadow-md p-4 flex flex-col justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-1">
                  Lavorazioni generate
                  {generatedPending > 0 && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 align-middle">
                      {generatedPending} da revisionare
                    </span>
                  )}
                </h2>
                <p className="text-xs text-gray-500 mb-3">
                  Richieste lette in automatico dalla casella assistenza, da
                  controllare prima di diventare lavorazioni.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/lavorazioni/generate')}
                className="mt-auto px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition"
              >
                Revisiona le proposte
              </button>
            </Card>
          )}
        </div>

        {/* Bottone torna indietro (mobile) */}
        <button
          onClick={() => navigate('/dashboard')}
          className="md:hidden mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
        >
          ← Torna alla dashboard
        </button>

        {/* SEZIONE TABELLONE LAVORAZIONI */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <h2 className="text-sm md:text-base font-semibold text-gray-800">
                Elenco lavorazioni (attive, sospese, riassegnate, terminate)
              </h2>
              <p className="text-[11px] md:text-xs text-gray-500">
                Usa i filtri per cercare per codice, cliente, luogo, tecnico,
                date, ecc. Lo stato è evidenziato con colori diversi.
              </p>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-emerald-400" /> Attiva
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-purple-400" /> Riassegnata
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-amber-400" /> Sospesa
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-gray-400" /> Terminata
              </span>
            </div>
          </div>

          {/* Filtri (user friendly) */}
          <div className="space-y-3">
            {/* barra unica + select */}
            <div className="flex flex-col md:flex-row gap-2 md:items-end">
              <div className="w-full">
                <label className="block text-gray-500 mb-1 text-xs">Filtra</label>
                <input
                  type="text"
                  name="q"
                  value={filters.q}
                  onChange={handleFilterChange}
                  placeholder="Cerca per codice, cliente, luogo, tecnico, modello, seriale..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs"
                />
              </div>

              <div className="w-full md:w-52">
                <label className="block text-gray-500 mb-1 text-xs">Tipo</label>
                <select
                  name="tipo"
                  value={filters.tipo}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-200 rounded-lg px-2 py-2 text-xs bg-white"
                >
                  <option value="tutti">Tutti</option>
                  <option value="intervento">Intervento</option>
                  <option value="manutenzione">Manutenzione</option>
                </select>
              </div>

              <div className="w-full md:w-60">
                <label className="block text-gray-500 mb-1 text-xs">Preventivo</label>
                <select
                  name="statoPreventivo"
                  value={filters.statoPreventivo}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-200 rounded-lg px-2 py-2 text-xs bg-white"
                >
                  <option value="tutti">Tutti</option>
                  <option value="Preventivo da fare">Preventivo da fare</option>
                  <option value="Preventivo fatto">Preventivo fatto</option>
                  <option value="A consuntivo">A consuntivo</option>
                  <option value="Contratto">Contratto</option>
                </select>
              </div>
            </div>

            {/* box date */}
            <div className="grid md:grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-xl p-3">
                <div className="text-xs font-semibold text-gray-700 mb-2">Data ricezione</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    name="dataRicezioneDa"
                    value={filters.dataRicezioneDa}
                    onChange={handleFilterChange}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                  />
                  <input
                    type="date"
                    name="dataRicezioneA"
                    value={filters.dataRicezioneA}
                    onChange={handleFilterChange}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                  />
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-3">
                <div className="text-xs font-semibold text-gray-700 mb-2">Data intervento</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    name="dataEseguitaDa"
                    value={filters.dataEseguitaDa}
                    onChange={handleFilterChange}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                  />
                  <input
                    type="date"
                    name="dataEseguitaA"
                    value={filters.dataEseguitaA}
                    onChange={handleFilterChange}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Tabella */}
          <div className="border border-gray-200 rounded-xl max-h-[520px] overflow-auto">
            
            {loadingTable ? (
              <div className="p-4 text-xs text-gray-600">
                Caricamento lavorazioni...
              </div>
            ) : errorTable ? (
              <div className="p-4 text-xs text-red-600">
                {errorTable}
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-4 text-xs text-gray-600">
                Nessuna lavorazione trovata con i filtri impostati.
              </div>
            ) : (
              <table className="min-w-max w-full border-separate border-spacing-0 text-[11px]">
              <colgroup>
                <col style={{ width: 110 }} /><col style={{ width: 140 }} /><col style={{ width: 190 }} />
              </colgroup>

              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="px-3 py-2 border-b border-r border-gray-200 bg-gray-50 sticky top-0 left-0 z-30">
                    Stato
                  </th>
                  <th className="px-3 py-2 border-b border-r border-gray-200 bg-gray-50 sticky top-0 left-[110px] z-30">
                    Cod. interno
                  </th>
                  <th className="px-3 py-2 border-b border-r border-gray-200 bg-gray-50 sticky top-0 left-[250px] z-30 shadow-[4px_0_0_0_rgba(0,0,0,0.04)]">
                    Cod. esterno
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Tipo lav.
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Manut. con prev
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Preventivo
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Consuntivo
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Rif. preventivo
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Stato ordine
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Materiale arrivato
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Azienda / Cliente
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Luogo
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Tipo
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Produttore
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Modello
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Seriale
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Inventario
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Presidio
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Assegnato a
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Data ricezione
                  </th>
                  <th className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                    Data intervento
                  </th>
                </tr>
              </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const codiceCompleto = formatInternalCode(o);

                    const eventsArray = Array.isArray(o.events)
                      ? o.events
                      : [];
                    const lastEvent =
                      eventsArray.length > 0
                        ? eventsArray[eventsArray.length - 1]
                        : null;

                    const tecnicoName = lastEvent?.assignedTo
                      ? formatUserLabel(lastEvent.assignedTo)
                      : formatUserLabel(o.assignedTo);

                    const dataIntervento =
                      lastEvent?.date ||
                      (Array.isArray(o.dates) && o.dates[0]) ||
                      o.dateIntervento ||
                      o.dataIntervento ||
                      null;

                    const dataRicezione =
                      o.receivedAt ||
                      o.dateReceived ||
                      o.dataRicezione ||
                      null;

                    const statusInfo = getStatusInfo(o);

                    return (
                      <tr
                        key={o._id}
                        className="border-t border-gray-100 hover:bg-gray-50"
                      >
                      <td className="px-3 py-2 align-top bg-white md:sticky md:left-0 md:z-10 border-b border-r border-gray-100">
                        <span className={`inline-flex px-2 py-1 rounded-full text-[10px] font-semibold ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap bg-white md:sticky md:left-[110px] md:z-10 border-b border-r border-gray-100">
                        {codiceCompleto}
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap bg-white md:sticky md:left-[250px] md:z-10 border-b border-r border-gray-100 shadow-[4px_0_0_0_rgba(0,0,0,0.04)]">
                        {o.externalCode || '—'}
                      </td>                    
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.type || '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.type === 'manutenzione' ? (
                            o.manutenzioneConPreventivo ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                Sì
                              </span>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.type === 'intervento'
                            ? o.preventivoStatus || '—'
                            : '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.type === 'intervento' && o.preventivoStatus === 'A consuntivo'
                            ? o.consuntivoStatus || '—'
                            : '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.rifPreventivo ? (
                            <span className="font-mono">{o.rifPreventivo}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.type === 'intervento' ? (
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                o.orderStatus === 'Ordine ricevuto'
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : o.orderStatus === 'Ordine non ricevuto'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-gray-100 text-gray-600 border border-gray-200'
                              }`}
                            >
                              {o.orderStatus || '—'}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.type === 'intervento' ? (
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                o.materialeArrivato === 'Si'
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : o.materialeArrivato === 'No'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-gray-100 text-gray-600 border border-gray-200'
                              }`}
                            >
                              {o.materialeArrivato || '—'}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        
                        <td className="px-3 py-2 align-top">
                          {o.companyName || o.cliente || '—'}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {o.place || o.luogo || '—'}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {o.equipmentType || o.tipoApparecchiatura || '—'}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {o.equipmentMaker || o.produttore || '—'}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {o.equipmentModel || o.modello || '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.equipmentSerial || o.seriale || '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {o.equipmentInventory || o.inventario || '—'}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {o.equipmentPresidio || o.presidio || '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {tecnicoName || '—'}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {formatDate(dataRicezione)}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {formatDate(dataIntervento)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
             
          </div>
          <div className="flex justify-end pt-4">
            <button
              onClick={exportToExcel}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition"
            >
              Scarica excel tabella
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Lavorazioni;
